import './product-stock.scss';
import { useEffect, useState } from 'react';
import { Product, ProductLocationStock, ProductTrackingType } from '../../../../clients/quipli-service/product/product';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { Location } from '../../../../clients/quipli-service/location/location';
import { Spinner } from '../../../../components/spinner/spinner';
import Container from '../../../../components/container/container';
import { Typography } from '../../../../components/typography/typography';
import { Dictionary, isEqual, keyBy, keys } from 'lodash';
import { Toggle } from '../../../../components/form/toggle';
import { toast } from '../../../../util/toasts';
import { Button } from '../../../../components/form/button';
import { Card } from '../../../../components/card/card';

export const ProductStock = ({
  product,
  locations,
  refresh,
  saleSerialTrackingEnabled,
}: {
  product: Product;
  locations: Location[];
  refresh: () => any;
  saleSerialTrackingEnabled: boolean;
}) => {
  const { updateProductLocationStocks } = useAPI();
  const [locationStocks, setLocationStocks] = useState<Dictionary<ProductLocationStock>>();
  const [locationStocksSelected, setLocationStocksSelected] = useState<boolean>(!!product.stock);
  const [totalRentalStock, setTotalRentalStock] = useState<number>(product.rentalStock);
  const [totalSaleStock, setTotalSaleStock] = useState<number>(+product.saleStock);
  const defaultLocationStocks = product.locationIds.map((locationId) => {
    return { productId: product.id, locationId, rentalStock: 0, saleStock: 0 };
  });
  const showSaveButton =
    totalRentalStock !== (product.rentalStock ?? 0) ||
    totalSaleStock !== (+product.saleStock ?? 0) ||
    locationStocksSelected !== !!product.stock ||
    !isEqual(
      locationStocks,
      product.stock
        ? keyBy([...defaultLocationStocks, ...product.stock], 'locationId')
        : keyBy(defaultLocationStocks, 'locationId'),
    );

  // Force stock by location if the system has only one location
  // REMOVE AFTER ALL CUSTOMERS ARE MIGRATED TO STOCK BY LOCATION
  const forceStockByLocation = locationStocksSelected && locations.length === 1;

  function resetProductStock() {
    setLocationStocks(
      product.stock
        ? keyBy([...defaultLocationStocks, ...product.stock], 'locationId')
        : keyBy(defaultLocationStocks, 'locationId'),
    );
    setTotalRentalStock(product.rentalStock || 0);
    setTotalSaleStock(+product.saleStock || 0);
  }
  useEffect(() => {
    resetProductStock();
  }, [product, locationStocksSelected]);

  const rows =
    locationStocks &&
    keys(locationStocks).flatMap((locationId) => {
      const ls = locationStocks[locationId];
      if (
        !product.locationIds.includes(parseInt(locationId)) &&
        !locations.find((l) => l.id === parseInt(locationId))?.isActive
      ) {
        return [];
      }
      const cells = {
        location: (
          <>
            <Typography type="text-md">
              {locations.find((l) => l.id === ls.locationId)?.name}
              {product.locationIds.indexOf(ls.locationId) < 0 && <span className="text-red-500">*</span>}
            </Typography>
            {product.locationIds.indexOf(ls.locationId) < 0 && (
              <Typography type="text-xs" className="text-red-500">
                This location is not assigned to this product
              </Typography>
            )}
          </>
        ),
        rentalStock:
          product.allowRent && product.trackingType === ProductTrackingType.Quantity ? (
            <input
              placeholder="0"
              onWheel={(e) => (e.target as HTMLElement).blur()}
              type="number"
              value={locationStocks[ls.locationId].rentalStock}
              onChange={(rs) =>
                setLocationStocks({ ...locationStocks, [locationId]: { ...ls, rentalStock: rs.target.valueAsNumber } })
              }
              className={`${
                ls.rentalStock < 0 || product.locationIds.indexOf(ls.locationId) < 0 ? 'text-red-300' : ''
              }`}
            />
          ) : (
            <></>
          ),
        saleStock:
          product.allowSale && (product.trackingType === ProductTrackingType.Quantity || !saleSerialTrackingEnabled) ? (
            <input
              placeholder="0"
              onWheel={(e) => (e.target as HTMLElement).blur()}
              type="number"
              value={locationStocks[ls.locationId].saleStock}
              onChange={(ss) =>
                setLocationStocks({ ...locationStocks, [locationId]: { ...ls, saleStock: ss.target.valueAsNumber } })
              }
              className={`${ls.saleStock < 0 || product.locationIds.indexOf(ls.locationId) < 0 ? 'text-red-300' : ''}`}
            />
          ) : (
            <></>
          ),
      };
      return [
        {
          locationId,
          ...cells,
          mobileHeader: <></>,
          fields: cells,
        },
      ];
    });

  useEffect(() => {
    if (locationStocks && locationStocksSelected) {
      const rentalStock = keys(locationStocks).reduce((acc, key) => {
        return acc + (locationStocks[key]?.rentalStock || 0);
      }, 0);
      const saleStock = keys(locationStocks).reduce((acc, key) => {
        return acc + (+locationStocks[key]?.saleStock || 0);
      }, 0);
      if (product.allowRent && product.trackingType === ProductTrackingType.Quantity) {
        setTotalRentalStock(rentalStock);
      }
      if (product.allowSale) {
        setTotalSaleStock(saleStock);
      }
    }
  }, [locationStocks]);

  async function saveProductStock() {
    if (totalRentalStock < 0 || totalSaleStock < 0 || (locationStocksSelected && !locationStocks)) {
      toast('Please enter valid stock quantities', 'error');
      return;
    }
    return updateProductLocationStocks(product.id, {
      productId: product.id,
      rentalStock: totalRentalStock,
      saleStock: totalSaleStock,
      locationStocks: locationStocksSelected && locationStocks ? Object.values(locationStocks) : undefined,
    })
      .then(() => {
        refresh();
        toast('Product stock updated', 'success');
      })
      .catch((err: any) => {
        console.error(err);
        toast(
          err.status === 400
            ? err.message?.msg ?? 'Please enter valid stock quantities'
            : 'Error saving stock quantities',
          'error',
        );
      });
  }
  return product && locations ? (
    <div className="product-stock">
      <div className="summary-row flex gap-1 flex-row mt-2">
        {product.allowRent && (
          <Card className="flex flex-column grow max-w-[300px]">
            <span className="text-[--var-gray-200]">Total Rental Stock</span>
            <span>{totalRentalStock}</span>
          </Card>
        )}
        {product.allowSale && (
          <Card className="flex flex-column grow max-w-[300px]">
            <span className="text-[--var-gray-200]">Total Sale Stock</span>
            <span>{totalSaleStock}</span>
          </Card>
        )}
      </div>
      {(product.trackingType === ProductTrackingType.Quantity || (product.allowSale && !saleSerialTrackingEnabled)) && (
        <>
          <div className="flex gap-6 mt-3 items-center">
            <Typography type="text-xl">Product Quantities</Typography>
            {!forceStockByLocation && (
              <div className="flex m-2">
                <Toggle
                  onClick={() => {
                    setLocationStocksSelected(!locationStocksSelected);
                    resetProductStock();
                  }}
                  checked={locationStocksSelected}
                />
                <div className="ml-2">
                  <Typography type="text-sm">Specify stock quantity by location</Typography>
                </div>
              </div>
            )}
          </div>
          <Container>
            <div className="pb-1 px-2 rounded-md w-fit">
              {locationStocksSelected && locationStocks ? (
                <div>
                  <div
                    className={`mx-4 mb-2 grid gap-x-6 gap-y-2 grid-cols-${
                      product.allowSale && product.allowRent && product.trackingType === ProductTrackingType.Quantity
                        ? '3'
                        : '2'
                    } items-center`}
                  >
                    <div className="text-left">Location</div>
                    {product.allowRent && product.trackingType === ProductTrackingType.Quantity && (
                      <div className="text-left">Rental Stock</div>
                    )}
                    {product.allowSale &&
                      (product.trackingType === ProductTrackingType.Quantity || !saleSerialTrackingEnabled) && (
                        <div className="text-left">Sale Stock</div>
                      )}
                    {rows?.map((row) => (
                      <>
                        <div className="text-left">{row.fields.location}</div>
                        {product.allowRent && product.trackingType === ProductTrackingType.Quantity && (
                          <div className="text-left">{row.fields.rentalStock}</div>
                        )}
                        {product.allowSale && <div className="text-left">{row.fields.saleStock}</div>}
                      </>
                    ))}
                  </div>
                  {showSaveButton && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          resetProductStock();
                          setLocationStocksSelected(!!product.stock);
                        }}
                        className="button-secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={Object.values(locationStocks).reduce((acc, ls) => {
                          return (
                            acc ||
                            ls.rentalStock < 0 ||
                            ls.saleStock < 0 ||
                            (product.locationIds.includes(ls.locationId) &&
                              !locations.find((l) => l.id === ls.locationId)?.isActive)
                          );
                        }, false)}
                        onClick={saveProductStock}
                        className="button-primary"
                      >
                        Save Changes
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex gap-8 mx-4">
                    {product.allowRent && product.trackingType === ProductTrackingType.Quantity && (
                      <div className="input-group">
                        <label>Rental Stock</label>
                        <input
                          placeholder="0"
                          onWheel={(e) => (e.target as HTMLElement).blur()}
                          type="number"
                          value={totalRentalStock}
                          onChange={(v) => setTotalRentalStock(v.target.valueAsNumber)}
                        />
                      </div>
                    )}

                    {product.allowSale && (
                      <div className="input-group">
                        <label>Sale Stock</label>
                        <input
                          placeholder="0"
                          onWheel={(e) => (e.target as HTMLElement).blur()}
                          type="number"
                          value={totalSaleStock}
                          onChange={(v) => setTotalSaleStock(v.target.valueAsNumber)}
                        />
                      </div>
                    )}
                  </div>
                  {showSaveButton && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => {
                          resetProductStock();
                          setLocationStocksSelected(!!product.stock);
                        }}
                        className="button-secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={totalRentalStock < 0 || totalSaleStock < 0}
                        onClick={saveProductStock}
                        className="button-primary"
                      >
                        Save Changes
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Container>
        </>
      )}
    </div>
  ) : (
    <Spinner />
  );
};
